import { AlertCircle, ShieldCheck } from 'lucide-react';
import { useEffect, useRef, useState, type FormEvent } from 'react';
import { useTranslation } from 'react-i18next';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import { Input } from '@/components/ui/input';
import { ApiError } from '@/lib/api';
import { useAuth } from '@/lib/auth';

/** קלט קוד בן 6 ספרות: רק ספרות, גם בהדבקה ("123 456" → "123456"). */
export const onlyDigits = (v: string) => v.replace(/\D/g, '').slice(0, 6);

/** הודעת שגיאה לשלב הקוד — לפי הסטטוס, בשפת הממשק (הודעת השרת באנגלית). */
export function mfaErrorKey(err: unknown): 'mfa.step.invalid' | 'mfa.step.expired' | 'mfa.step.locked' {
  if (err instanceof ApiError && err.status === 429) return 'mfa.step.locked';
  if (err instanceof ApiError && /expired|sign in again/i.test(err.message)) return 'mfa.step.expired';
  return 'mfa.step.invalid';
}

/**
 * שלב הקוד בכניסה (אחרי סיסמה נכונה או Google): קוד מהאפליקציה, או קוד שחזור,
 * ו"לזכור את המכשיר הזה ל-30 יום". שליחה אוטומטית כשהוקלדו 6 ספרות.
 */
export function MfaStep({
  ticket,
  onDone,
  onCancel,
}: {
  ticket: string;
  onDone: () => void | Promise<void>;
  onCancel: () => void;
}) {
  const { t } = useTranslation('auth');
  const { verifyMfa } = useAuth();
  const [useRecovery, setUseRecovery] = useState(false);
  const [code, setCode] = useState('');
  const [recoveryCode, setRecoveryCode] = useState('');
  // מסומן מראש: הסטנדרט הוא לא לשאול קוד בכל כניסה מאותו מכשיר (90 יום).
  const [remember, setRemember] = useState(true);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [expired, setExpired] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    inputRef.current?.focus();
  }, [useRecovery]);

  const submit = async (value = code) => {
    if (busy) return;
    if (useRecovery ? recoveryCode.trim().length < 8 : value.length !== 6) return;
    setBusy(true);
    setError(null);
    try {
      await verifyMfa({
        ticket,
        rememberDevice: remember,
        ...(useRecovery ? { recoveryCode: recoveryCode.trim() } : { code: value }),
      });
      await onDone();
    } catch (err) {
      const key = mfaErrorKey(err);
      setError(t(key));
      setExpired(key === 'mfa.step.expired');
      setCode('');
      inputRef.current?.focus();
    } finally {
      setBusy(false);
    }
  };

  const onSubmit = (e: FormEvent) => {
    e.preventDefault();
    void submit();
  };

  return (
    <form
      onSubmit={onSubmit}
      noValidate
      className="space-y-4 rounded-(--radius-xl) border border-border bg-surface-raised p-6 shadow-sm"
    >
      <div className="flex items-start gap-3">
        <div className="grid size-9 shrink-0 place-items-center rounded-(--radius-md) bg-accent-subtle text-accent">
          <ShieldCheck className="size-4" aria-hidden />
        </div>
        <div>
          <h2 className="text-sm font-semibold text-fg">{t('mfa.step.title')}</h2>
          <p className="text-xs text-fg-muted">
            {useRecovery ? t('mfa.step.recoveryHint') : t('mfa.step.description')}
          </p>
        </div>
      </div>

      {error ? (
        <div
          role="alert"
          className="flex items-start gap-2 rounded-(--radius-md) border border-danger-border bg-danger-subtle p-3 text-xs text-danger"
        >
          <AlertCircle className="mt-px size-4 shrink-0" aria-hidden />
          <span>{error}</span>
        </div>
      ) : null}

      {useRecovery ? (
        <div className="space-y-1.5">
          <label htmlFor="mfa-recovery" className="block text-xs font-medium text-fg-muted">
            {t('mfa.step.recoveryLabel')}
          </label>
          <Input
            ref={inputRef}
            id="mfa-recovery"
            dir="ltr"
            autoComplete="off"
            autoCapitalize="none"
            spellCheck={false}
            value={recoveryCode}
            onChange={(e) => setRecoveryCode(e.target.value)}
            className="font-mono tracking-wider"
          />
        </div>
      ) : (
        <div className="space-y-1.5">
          <label htmlFor="mfa-code" className="block text-xs font-medium text-fg-muted">
            {t('mfa.step.codeLabel')}
          </label>
          <Input
            ref={inputRef}
            id="mfa-code"
            dir="ltr"
            inputMode="numeric"
            autoComplete="one-time-code"
            pattern="[0-9]*"
            maxLength={7}
            value={code}
            onChange={(e) => {
              const next = onlyDigits(e.target.value);
              setCode(next);
              if (next.length === 6) void submit(next);
            }}
            className="text-center font-mono text-lg tracking-[0.5em]"
          />
        </div>
      )}

      <label className="flex items-center gap-2 text-xs text-fg-muted">
        <Checkbox checked={remember} onCheckedChange={(v) => setRemember(v === true)} aria-label={t('mfa.step.remember')} />
        {t('mfa.step.remember')}
      </label>

      {expired ? (
        <Button type="button" className="w-full" size="lg" onClick={onCancel}>
          {t('mfa.step.back')}
        </Button>
      ) : (
        <Button type="submit" loading={busy} className="w-full" size="lg">
          {t('mfa.step.submit')}
        </Button>
      )}

      <div className="flex flex-wrap items-center justify-between gap-2 text-xs">
        <button
          type="button"
          className="font-medium text-accent hover:underline"
          onClick={() => {
            setUseRecovery((v) => !v);
            setError(null);
          }}
        >
          {useRecovery ? t('mfa.step.useCode') : t('mfa.step.useRecovery')}
        </button>
        <button type="button" className="text-fg-subtle hover:underline" onClick={onCancel}>
          {t('mfa.step.back')}
        </button>
      </div>
    </form>
  );
}
